import { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { AmountInput, Dropdown, Icon, Input, Label } from '@components';
import { NOTES_MAX_LENGTH } from '@constants/generic';
import { invalidCharInNameRegex } from '@constants/regex';
import useNameLengthWarning from '@features/lead-management/hooks/useNameLengthWarning';

import NameLengthWarning from '../NameLengthWarning';
import useBranchOptions from './useBranchOptions';
import useQuickCollectAmountLimits from './useQuickCollectAmountLimits';
import {
  getMoveToCofeeNameError,
  isMoveToCofeeNameInvalid
} from './validation';

import theme from '../../../../../themes/colors';

interface QuickCollectFieldsProps {
  leadName: string;
  name: string;
  branchId: string;
  amount: string;
  notes: string;
  nameApiError?: string;
  onNameChange: (val: string) => void;
  onBranchSelect: (value: string, label: string) => void;
  onAmountChange: (val: string) => void;
  onNotesChange: (val: string) => void;
  afterName?: ReactNode;
  afterBranch?: ReactNode;
}

const QuickCollectFields = ({
  leadName,
  name,
  branchId,
  amount,
  notes,
  nameApiError,
  onNameChange,
  onBranchSelect,
  onAmountChange,
  onNotesChange,
  afterName,
  afterBranch
}: QuickCollectFieldsProps) => {
  const branchOptions = useBranchOptions();
  const { minAmount, maxAmount } = useQuickCollectAmountLimits();
  const { orgConfig } = useSelector((state: any) => state.user);
  const notesMaxLength =
    orgConfig?.quick_collect_note_max_length || NOTES_MAX_LENGTH;

  const showNameError =
    (name.length > 0 && isMoveToCofeeNameInvalid(name)) || !!nameApiError;
  const nameErrorMessage = nameApiError || getMoveToCofeeNameError(name);

  const {
    maxLength: nameMaxLength,
    showWarning: showNameWarning,
    warningMessage: nameWarningMessage,
    handlePaste: handleNamePaste,
    handleChange: handleNameChange,
    handleBlur: handleNameBlur
  } = useNameLengthWarning({ hasError: showNameError });

  const handleNameInputChange = (val: string) => {
    handleNameChange();
    onNameChange(val);
  };

  const amountNum = Number(amount);
  const showAmountError =
    amount !== '' && (amountNum < minAmount || amountNum > maxAmount);
  const showNotesError = notes.length > 0 && notes.trim().length < 3;

  const handleBranchSelect = (value: string) => {
    const branch = branchOptions.find((b) => b.value === value);
    onBranchSelect(value, branch?.label || '');
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-cofeeNeutral7">Configure details here:</p>
      <div className="relative">
        <Label
          label="Name"
          isRequired
          className="!mb-2 !text-sm !font-semibold"
        />
        <Input
          value={name}
          handleInputChange={handleNameInputChange}
          inputRegex={invalidCharInNameRegex}
          placeholder={leadName}
          inputStyle="bg-white"
          maxLength={nameMaxLength}
          onPaste={handleNamePaste}
          onBlur={handleNameBlur}
          error={showNameError}
          errorMessage={nameErrorMessage}
        />
        <NameLengthWarning
          show={showNameWarning}
          message={nameWarningMessage}
        />
      </div>
      {afterName}
      <div>
        <Label
          label="Branch"
          isRequired
          className="!mb-2 !text-sm !font-semibold"
        />
        <Dropdown
          currentFilter={branchId}
          filterOptions={branchOptions}
          onSelectFilter={handleBranchSelect}
          className="h-12 w-full !rounded-lg"
          optionsStyle="!w-full max-h-56 overflow-y-auto"
          labelStyle="truncate"
          optionItemClassName="break-all"
          placeholder="Select Branch"
          searchBarPlaceholder="Search branch"
          usePortal
        />
      </div>
      {afterBranch}
      <div>
        <Label
          label="Amount"
          isRequired
          className="!mb-2 !text-sm !font-semibold"
        />
        <AmountInput
          value={amount}
          onAmountChange={onAmountChange}
          placeholder="Enter amount"
          error={showAmountError}
        />
        <div className="mt-1 flex items-center gap-1 text-sm text-cofeeGreyBlue6">
          <Icon
            name="alert-circle"
            stroke={theme.subText}
            size="none"
            className="-mt-0.5"
          />
          Allowed amount is between {minAmount} and {maxAmount}
        </div>
      </div>
      <div className="pb-5">
        <Label
          label="Notes"
          isRequired
          className="!mb-2 !text-sm !font-semibold"
        />
        <Input
          value={notes}
          handleInputChange={onNotesChange}
          placeholder="Add Notes : Eg: Admission Fee for batch 7"
          inputStyle="bg-white"
          maxLength={notesMaxLength}
          error={showNotesError}
          errorMessage="Notes must be at least 3 characters"
        />
      </div>
    </div>
  );
};

export default QuickCollectFields;
